import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface ContratanteContatos {
  email?: string;
  email_principal?: string;
  telefone?: string;
  responsavel?: string;
}

export interface NetContratantePerfil {
  id: string;
  user_id: string;
  contrato_id: string | null;
  cnpj: string;
  nome_empresa: string;
  contatos: ContratanteContatos | null;
  documentos: Record<string, unknown> | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export function useNetContratantePerfil(contratoId?: string) {
  return useQuery({
    queryKey: ['net-contratante-perfil', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('net_contratante_perfil')
        .select('*')
        .eq('contrato_id', contratoId!)
        .maybeSingle();

      if (error) throw error;
      return data as NetContratantePerfil | null;
    },
    enabled: !!contratoId,
  });
}

export function useUpsertContratantePerfil() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      contrato_id: string;
      nome_empresa: string;
      cnpj: string;
      contatos: ContratanteContatos;
    }) => {
      if (!user) throw new Error('Usuário não autenticado');

      // Check if profile already exists for this contract
      const { data: existing } = await supabase
        .from('net_contratante_perfil')
        .select('id')
        .eq('contrato_id', params.contrato_id)
        .maybeSingle();

      if (existing) {
        const { data, error } = await supabase
          .from('net_contratante_perfil')
          .update({
            nome_empresa: params.nome_empresa,
            cnpj: params.cnpj,
            contatos: params.contatos as any,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('net_contratante_perfil')
          .insert({
            user_id: user.id,
            contrato_id: params.contrato_id,
            nome_empresa: params.nome_empresa,
            cnpj: params.cnpj,
            contatos: params.contatos as any,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['net-contratante-perfil', vars.contrato_id] });
      toast({ title: 'Dados do contratante salvos' });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao salvar contratante', description: err.message, variant: 'destructive' });
    },
  });
}
